import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { todayDateOnlyString } from '../../utils/dateUtils';

interface Credential {
  type: 'DBS Certificate' | 'DBS Update Service' | 'Insurance' | 'WorkPermit';
  expiryDate?: string | null;
  details?: string;
  number?: string;
  provider?: string;
  subscriptionId?: string;
  daysUntilExpiry?: number;
  status: 'valid' | 'expiring' | 'expired' | 'clear' | 'changes_detected' | 'check_overdue' | 'not_checked';
  message?: string;
  lastChecked?: string;
  nextCheckDue?: string;
  daysSinceLastCheck?: number;
  daysUntilNextCheck?: number;
  changesDetected?: boolean;
  unresolvedChangesCount?: number;
}

interface Submission {
  credentialType: string;
  submittedDate: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  reviewedDate?: string;
  reviewNotes?: string;
}

export default function CredentialRenewal() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [openDialog, setOpenDialog] = useState(false);

  // Form state
  const [credentialType, setCredentialType] = useState<'DBS' | 'DBS Certificate' | 'Insurance' | 'WorkPermit'>('DBS');
  const [dbsNumber, setDbsNumber] = useState('');
  const [dbsIssueDate, setDbsIssueDate] = useState(''); // Issue/print date from certificate
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insuranceNumber, setInsuranceNumber] = useState('');
  const [insuranceExpiryDate, setInsuranceExpiryDate] = useState('');
  const [workPermitDetails, setWorkPermitDetails] = useState('');
  const [workPermitExpiryDate, setWorkPermitExpiryDate] = useState('');
  const [workPermitNoExpiry, setWorkPermitNoExpiry] = useState(false);
  const [certificate, setCertificate] = useState<File | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [credResponse, submissionsResponse] = await Promise.all([
        api.get('/api/counsellors/my-credentials'),
        api.get('/api/credential-reactivation/my-submissions'),
      ]);

      setCredentials(credResponse.data.credentials || []);
      setSubmissions(submissionsResponse.data.submissions || []);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Failed to load credential information');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF or image file (JPG, PNG)');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setCertificate(file);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!certificate) {
      setError('Please upload your certificate');
      return;
    }

    // Validate required fields
    if (credentialType === 'DBS') {
      if (!dbsNumber || !dbsIssueDate) {
        setError('Please provide DBS number and issue date');
        return;
      }
    } else if (credentialType === 'Insurance') {
      if (!insuranceProvider || !insuranceNumber || !insuranceExpiryDate) {
        setError('Please provide insurance provider, number, and expiry date');
        return;
      }
    } else if (credentialType === 'WorkPermit') {
      if (!workPermitDetails || (!workPermitNoExpiry && !workPermitExpiryDate)) {
        setError('Please provide work permit details and an expiry date, or mark the permit as having no expiry date');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      const formData = new FormData();
      formData.append('certificate', certificate);
      formData.append('credentialType', credentialType);

      if (credentialType === 'DBS') {
        formData.append('dbsNumber', dbsNumber);
        formData.append('dbsIssueDate', dbsIssueDate); // Backend will calculate expiry (issue + 3 years)
      } else if (credentialType === 'Insurance') {
        formData.append('insuranceProvider', insuranceProvider);
        formData.append('insuranceNumber', insuranceNumber);
        formData.append('insuranceExpiryDate', insuranceExpiryDate);
      } else if (credentialType === 'WorkPermit') {
        formData.append('workPermitDetails', workPermitDetails);
        formData.append('workPermitNoExpiry', String(workPermitNoExpiry));
        formData.append('workPermitExpiryDate', workPermitNoExpiry ? '' : workPermitExpiryDate);
      }

      await api.post('/api/credential-reactivation/submit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess('Credential submitted successfully! Our admin team will review it shortly.');
      setOpenDialog(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit credential. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setCredentialType('DBS');
    setDbsNumber('');
    setDbsIssueDate('');
    setInsuranceProvider('');
    setInsuranceNumber('');
    setInsuranceExpiryDate('');
    setWorkPermitDetails('');
    setWorkPermitExpiryDate('');
    setWorkPermitNoExpiry(false);
    setCertificate(null);
    setError(null);
  };

  const handleOpenDialog = (type?: 'DBS' | 'DBS Certificate' | 'DBS Update Service' | 'Insurance' | 'WorkPermit') => {
    resetForm();
    if (type) {
      // Map 'DBS Certificate' and 'DBS Update Service' to 'DBS' for the form
      const formType = type === 'DBS Certificate' || type === 'DBS Update Service' ? 'DBS' : type;
      setCredentialType(formType);
    }
    setOpenDialog(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid': return 'success';
      case 'expiring': return 'warning';
      case 'expired': return 'error';
      case 'Pending': return 'info';
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 0 }}>
      {success && (
        <Alert severity="success" sx={{
          mb: 3,
          backgroundColor: 'rgba(74, 222, 128, 0.1)',
          color: '#4ade80',
          border: '1px solid rgba(74, 222, 128, 0.3)',
          '& .MuiAlert-icon': { color: '#4ade80' }
        }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && !openDialog && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            '& .MuiAlert-icon': {
              color: '#ef4444'
            }
          }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Credential Management */}
      <div className="glass-card overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-white/10">
          <Typography className="theme-text-primary" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
            Credential Management
          </Typography>
        </div>

        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
          {credentials.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(255, 255, 255, 0.5)' }}>
              <Typography variant="body2">No credentials found</Typography>
            </Box>
          ) : (
            credentials.map((cred, index) => (
              <Card
                key={index}
                sx={{
                  mb: 2,
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600, fontSize: '1rem' }}>
                      {cred.type}
                    </Typography>
                    <Chip
                      label={cred.status === 'clear' ? 'CLEAR' : 
                             cred.status === 'changes_detected' ? 'CHANGES' :
                             cred.status === 'check_overdue' ? 'OVERDUE' :
                             cred.status === 'not_checked' ? 'NOT CHECKED' :
                             cred.status.toUpperCase()}
                      size="small"
                      sx={{
                        height: '24px',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: cred.status === 'valid' || cred.status === 'clear' ? '#4caf50' : 
                                        cred.status === 'expiring' || cred.status === 'check_overdue' ? '#ff9800' : 
                                        cred.status === 'changes_detected' ? '#f44336' : '#9e9e9e',
                        color: '#fff'
                      }}
                    />
                  </Box>

                  {(cred.number || cred.provider || cred.subscriptionId) && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', mb: 1 }}>
                      {cred.number && cred.provider 
                        ? `#${cred.number} | ${cred.provider}`
                        : cred.number 
                        ? `#${cred.number}`
                        : cred.provider 
                        ? cred.provider
                        : `ID: ${cred.subscriptionId}`}
                    </Typography>
                  )}

                  {cred.expiryDate ? (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem', display: 'block' }}>
                        Expiry Date
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem' }}>
                        {new Date(cred.expiryDate).toLocaleDateString('en-GB')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem' }}>
                        {cred.daysUntilExpiry !== undefined && cred.daysUntilExpiry > 0 
                          ? `${cred.daysUntilExpiry} days remaining`
                          : cred.daysUntilExpiry !== undefined
                          ? `Expired ${Math.abs(cred.daysUntilExpiry)} days ago`
                          : ''}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem', display: 'block', mb: 1 }}>
                      {cred.message || 'No expiry - continuous monitoring'}
                    </Typography>
                  )}

                  {(cred.daysUntilExpiry !== undefined && cred.daysUntilExpiry <= 90) && (
                    <Button
                      onClick={() => handleOpenDialog(cred.type)}
                      variant="contained"
                      size="small"
                      fullWidth
                      sx={{
                        mt: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        backgroundColor: '#2196f3',
                        '&:hover': { backgroundColor: '#1976d2' }
                      }}
                    >
                      Renew Credential
                    </Button>
                  )}

                  {cred.changesDetected && (
                    <Alert severity="warning" sx={{
                      mt: 1, py: 0.5, fontSize: '0.75rem',
                      backgroundColor: 'rgba(245, 158, 11, 0.1)',
                      color: '#fcd34d',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      '& .MuiAlert-icon': { color: '#fbbf24' }
                    }}>
                      Requires Review
                    </Alert>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Box>

        {/* Desktop Table View */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">Credential Type</th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">Details</th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">Expiry Date</th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">Status</th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {credentials.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 sm:px-6 py-3 sm:py-4 text-center theme-text-secondary">
                    No credentials found
                  </td>
                </tr>
              ) : (
                credentials.map((cred, index) => (
                  <tr key={index} className="hover:bg-white/5 transition-colors">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <Typography className="theme-text-primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}>
                        {cred.type}
                      </Typography>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {cred.number && cred.provider 
                          ? `#${cred.number} | ${cred.provider}`
                          : cred.number 
                          ? `#${cred.number}`
                          : cred.provider 
                          ? cred.provider
                          : cred.subscriptionId 
                          ? `ID: ${cred.subscriptionId}`
                          : ''}
                      </Typography>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      {cred.expiryDate ? (
                        <>
                          <Typography className="theme-text-primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {new Date(cred.expiryDate).toLocaleDateString('en-GB')}
                          </Typography>
                          <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' }, mt: 0.5 }}>
                            {cred.daysUntilExpiry !== undefined && cred.daysUntilExpiry > 0 
                              ? `${cred.daysUntilExpiry} days remaining`
                              : cred.daysUntilExpiry !== undefined
                              ? `Expired ${Math.abs(cred.daysUntilExpiry)} days ago`
                              : ''
                            }
                          </Typography>
                        </>
                      ) : (
                        <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}>
                          {cred.message || 'No expiry - continuous monitoring'}
                        </Typography>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <Chip
                        label={cred.status === 'clear' ? 'CLEAR' : 
                               cred.status === 'changes_detected' ? 'CHANGES' :
                               cred.status === 'check_overdue' ? 'OVERDUE' :
                               cred.status === 'not_checked' ? 'NOT CHECKED' :
                               cred.status.toUpperCase()}
                        sx={{
                          height: { xs: '22px', sm: '26px' },
                          borderRadius: '13px',
                          fontSize: { xs: '0.625rem', sm: '0.75rem' },
                          fontWeight: 600,
                          backgroundColor: cred.status === 'valid' || cred.status === 'clear' ? '#4caf50' : 
                                          cred.status === 'expiring' || cred.status === 'check_overdue' ? '#ff9800' : 
                                          cred.status === 'changes_detected' ? '#f44336' :
                                          '#9e9e9e',
                          color: '#fff'
                        }}
                      />
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      {(cred.daysUntilExpiry !== undefined && cred.daysUntilExpiry <= 90) && (
                        <button
                          onClick={() => handleOpenDialog(cred.type)}
                          className="text-blue-400 font-semibold hover:text-blue-300 transition-colors text-xs sm:text-sm"
                        >
                          Renew Credential
                        </button>
                      )}
                      {cred.changesDetected && (
                        <Typography className="text-red-400" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' }, fontWeight: 600 }}>
                          ⚠️ Requires Review
                        </Typography>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </Box>
      </div>

      {/* Submission History */}
      {submissions.length > 0 && (
        <Box sx={{ mt: { xs: 3, sm: 4 } }}>
        <div className="glass-card overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-white/10">
            <Typography className="theme-text-primary" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              Renewal Submissions
            </Typography>
          </div>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
            {submissions.map((sub, index) => (
              <Card
                key={index}
                sx={{
                  mb: 2,
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600, fontSize: '1rem' }}>
                      {sub.credentialType}
                    </Typography>
                    <Chip
                      label={sub.status}
                      size="small"
                      sx={{
                        height: '24px',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: getStatusColor(sub.status) === 'success' ? '#4caf50' :
                                       getStatusColor(sub.status) === 'error' ? '#f44336' :
                                       getStatusColor(sub.status) === 'info' ? '#2196f3' : '#9e9e9e',
                        color: '#fff'
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem', display: 'block' }}>
                      Submitted
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem' }}>
                      {new Date(sub.submittedDate).toLocaleDateString('en-GB')}
                    </Typography>
                  </Box>

                  {sub.reviewNotes && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem', display: 'block' }}>
                        Review Notes
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.875rem' }}>
                        {sub.reviewNotes}
                      </Typography>
                    </Box>
                  )}

                  {sub.reviewedDate && (
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem' }}>
                      Reviewed: {new Date(sub.reviewedDate).toLocaleDateString('en-GB')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">Credential Type</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">Submitted Date</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">Status</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold theme-text-secondary uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {submissions.map((sub, index) => (
                  <tr key={index} className="hover:bg-white/5 transition-colors">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <Typography className="theme-text-primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}>
                        {sub.credentialType}
                      </Typography>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {new Date(sub.submittedDate).toLocaleDateString()}
                      </Typography>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <Chip
                        label={sub.status}
                        sx={{
                          height: { xs: '22px', sm: '26px' },
                          borderRadius: '13px',
                          fontSize: { xs: '0.625rem', sm: '0.75rem' },
                          fontWeight: 600,
                          backgroundColor: getStatusColor(sub.status) === 'success' ? '#4caf50' :
                                         getStatusColor(sub.status) === 'error' ? '#f44336' :
                                         getStatusColor(sub.status) === 'info' ? '#2196f3' : '#9e9e9e',
                          color: '#fff'
                        }}
                      />
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {sub.reviewNotes || '-'}
                      </Typography>
                      {sub.reviewedDate && (
                        <Typography className="theme-text-secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' }, mt: 0.5 }}>
                          Reviewed: {new Date(sub.reviewedDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </Box>
        </div>
        </Box>
      )}

      {/* Renewal Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e293b',
            backgroundImage: 'none',
            color: 'rgba(255, 255, 255, 0.9)'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'rgba(255, 255, 255, 0.9)',
          fontWeight: 700
        }}>
          Renew Credential
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                '& .MuiAlert-icon': {
                  color: '#ef4444'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <FormControl 
              fullWidth 
              sx={{ 
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3'
                  }
                },
                '& .MuiSelect-icon': {
                  color: 'rgba(255, 255, 255, 0.7)'
                }
              }}
            >
              <InputLabel>Credential Type *</InputLabel>
              <Select
                value={credentialType}
                onChange={(e) => setCredentialType(e.target.value as any)}
                label="Credential Type *"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#1e293b',
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& .MuiMenuItem-root': {
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(33, 150, 243, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 0.3)'
                          }
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="DBS">DBS Certificate</MenuItem>
                <MenuItem value="Insurance">Professional Indemnity Insurance</MenuItem>
                <MenuItem value="WorkPermit">Work Permit</MenuItem>
              </Select>
            </FormControl>

            {credentialType === 'DBS' && (
              <>
                <TextField
                  fullWidth
                  label="DBS Certificate Number *"
                  value={dbsNumber}
                  onChange={(e) => setDbsNumber(e.target.value)}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                    }
                  }}
                  required
                />
                <TextField
                  fullWidth
                  type="date"
                  label="DBS Issue Date *"
                  value={dbsIssueDate}
                  onChange={(e) => setDbsIssueDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: todayDateOnlyString() }}
                  helperText="Enter the issue/print date from your DBS certificate. Expiry will be calculated automatically (3 years)."
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                    }
                  }}
                  required
                />
              </>
            )}

            {credentialType === 'Insurance' && (
              <>
                <TextField
                  fullWidth
                  label="Insurance Provider *"
                  value={insuranceProvider}
                  onChange={(e) => setInsuranceProvider(e.target.value)}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                    }
                  }}
                  required
                />
                <TextField
                  fullWidth
                  label="Policy Number *"
                  value={insuranceNumber}
                  onChange={(e) => setInsuranceNumber(e.target.value)}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                    }
                  }}
                  required
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Policy Expiry Date *"
                  value={insuranceExpiryDate}
                  onChange={(e) => setInsuranceExpiryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                    }
                  }}
                  required
                />
              </>
            )}

            {credentialType === 'WorkPermit' && (
              <>
                <TextField
                  fullWidth
                  label="Work Permit Details *"
                  value={workPermitDetails}
                  onChange={(e) => setWorkPermitDetails(e.target.value)}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                    }
                  }}
                  required
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={workPermitNoExpiry}
                      onChange={(e) => {
                        setWorkPermitNoExpiry(e.target.checked);
                        if (e.target.checked) setWorkPermitExpiryDate('');
                      }}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-checked': { color: '#2196f3' },
                      }}
                    />
                  }
                  label="No expiry date (indefinite right to work)"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.9)' } }}
                />
                <TextField
                  fullWidth
                  type="date"
                  label={workPermitNoExpiry ? 'Work Permit Expiry Date' : 'Work Permit Expiry Date *'}
                  value={workPermitExpiryDate}
                  onChange={(e) => setWorkPermitExpiryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={workPermitNoExpiry}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                    }
                  }}
                  required={!workPermitNoExpiry}
                />
              </>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Upload Certificate *
              </Typography>
              <Paper
                sx={{
                  border: 2,
                  borderStyle: 'dashed',
                  borderColor: certificate ? '#4caf50' : 'rgba(255, 255, 255, 0.23)',
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: certificate ? '#4caf50' : '#2196f3',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
                onClick={() => document.getElementById('renewal-certificate-upload')?.click()}
              >
                <input
                  id="renewal-certificate-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <UploadIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.5)', mb: 1 }} />
                {certificate ? (
                  <Typography sx={{ color: '#4caf50', fontWeight: 600 }}>
                    {certificate.name}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" fontWeight="medium" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      Click to upload certificate
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      PDF or Image (Max 10MB)
                    </Typography>
                  </>
                )}
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            disabled={submitting}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !certificate}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2'
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            {submitting ? <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.5)' }} /> : 'Submit for Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
